import type { OrderStatus, PaymentType } from '@telestore/contracts';
import { AppError } from '@telestore/shared';

/**
 * The order state machine (CLAUDE.md §3 `order`). It is deterministic and it
 * is the only place a status may change: every write path in the API and the
 * bot goes through `assertTransition`.
 *
 * Three branches out of `pending_confirm`, one per vertical flow (§4: the
 * verticals differ in their checkout FLOW, not in having their own machine):
 *
 *   prepaid   pending_confirm → awaiting_payment → paid → fulfilling → completed
 *   COD       pending_confirm → confirmed → shipped → collected → remitted → completed
 *   booking   pending_confirm → [awaiting_payment →] scheduled → completed
 *
 * `shipped` is shared: a prepaid order that goes out with a courier gets the
 * same shipment record and the same `order.shipped` notification, it just
 * skips the collect/remit legs because the money already landed.
 *
 * The booking branch reaches `scheduled` from either side — straight from
 * `pending_confirm` when the appointment is booked on trust, or via `paid`
 * when §4's deposit holds the slot. `no_show` is the honest end for an
 * appointment nobody attended: it is not a cancellation (the slot was lost)
 * and it is not a completion (no service was given), and a salon deciding
 * whether to take deposits from a customer needs to be able to tell.
 *
 * `cancelled` is pre-ship only. `refunded` is post-payment only. Both are
 * terminal.
 */

export const TERMINAL_STATUSES: readonly OrderStatus[] = [
  'completed',
  'cancelled',
  'refunded',
  // Nothing follows a no-show but a refund decision, handled below.
  'no_show',
];

/** Statuses where stock is held for this order and must be released on cancel. */
export const STOCK_HELD_STATUSES: readonly OrderStatus[] = [
  'pending_confirm',
  'awaiting_payment',
  'paid',
  'confirmed',
  'fulfilling',
  'shipped',
  'collected',
  'remitted',
  'scheduled',
  'completed',
];

/** Statuses that mean the shop has actually received the customer's money. */
export const PAID_STATUSES: readonly OrderStatus[] = [
  'paid',
  'fulfilling',
  'shipped',
  'collected',
  'remitted',
  'completed',
];

/**
 * Statuses that hold a slot on a resource's calendar (§4 booking).
 *
 * A slot is claimed the moment the order exists, not when it is paid —
 * otherwise two customers could be mid-checkout for the same 3pm. §4's
 * auto-release after 30 minutes unpaid is what stops an abandoned checkout
 * holding it forever.
 */
export const SLOT_HELD_STATUSES: readonly OrderStatus[] = [
  'pending_confirm',
  'awaiting_payment',
  'paid',
  'scheduled',
  'completed',
  'no_show',
];

interface TransitionRule {
  to: OrderStatus;
  /** When set, the transition only exists for this payment branch. */
  paymentType?: PaymentType;
}

const TRANSITIONS: Record<OrderStatus, TransitionRule[]> = {
  draft: [{ to: 'pending_confirm' }, { to: 'cancelled' }],

  pending_confirm: [
    { to: 'awaiting_payment', paymentType: 'prepaid' },
    { to: 'confirmed', paymentType: 'cod' },
    // An appointment booked on trust, with no deposit asked for (§4).
    { to: 'scheduled', paymentType: 'prepaid' },
    { to: 'cancelled' },
  ],

  // Prepaid branch
  awaiting_payment: [{ to: 'paid' }, { to: 'cancelled' }],
  // §4: the deposit confirming is what turns a held slot into an appointment.
  paid: [{ to: 'fulfilling' }, { to: 'scheduled' }, { to: 'cancelled' }, { to: 'refunded' }],
  fulfilling: [{ to: 'shipped' }, { to: 'completed' }, { to: 'cancelled' }, { to: 'refunded' }],

  // COD branch
  confirmed: [{ to: 'shipped' }, { to: 'cancelled' }],
  shipped: [
    { to: 'collected', paymentType: 'cod' },
    { to: 'completed', paymentType: 'prepaid' },
    { to: 'refunded' },
  ],
  collected: [{ to: 'remitted' }, { to: 'refunded' }],
  remitted: [{ to: 'completed' }, { to: 'refunded' }],

  /**
   * Services branch. A scheduled appointment ends one of three ways: it
   * happened, nobody came, or it was called off. Rescheduling is NOT a
   * transition — it moves the slot hold and leaves the order in `scheduled`,
   * because a customer moving their 3pm to 4pm has not changed anything about
   * the order except when it is.
   */
  scheduled: [{ to: 'completed' }, { to: 'no_show' }, { to: 'cancelled' }, { to: 'refunded' }],
  no_show: [{ to: 'refunded' }, { to: 'completed' }],

  // Terminal
  completed: [{ to: 'refunded' }],
  cancelled: [],
  refunded: [],
};

export function allowedTransitions(from: OrderStatus, paymentType: PaymentType): OrderStatus[] {
  return TRANSITIONS[from]
    .filter((rule) => rule.paymentType === undefined || rule.paymentType === paymentType)
    .map((rule) => rule.to);
}

export function canTransition(
  from: OrderStatus,
  to: OrderStatus,
  paymentType: PaymentType,
): boolean {
  return allowedTransitions(from, paymentType).includes(to);
}

export function assertTransition(
  from: OrderStatus,
  to: OrderStatus,
  paymentType: PaymentType,
): void {
  if (!canTransition(from, to, paymentType)) {
    throw new AppError(
      'invalid_transition',
      `Cannot move a ${paymentType} order from "${from}" to "${to}"`,
      { details: { from, to, paymentType, allowed: allowedTransitions(from, paymentType) } },
    );
  }
}

export function isTerminal(status: OrderStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

export function holdsStock(status: OrderStatus): boolean {
  return STOCK_HELD_STATUSES.includes(status);
}

export function isPaid(status: OrderStatus): boolean {
  return PAID_STATUSES.includes(status);
}

export function holdsSlot(status: OrderStatus): boolean {
  return SLOT_HELD_STATUSES.includes(status);
}

/**
 * Every placed order lands in `pending_confirm`. A shop with
 * `auto_confirm_orders` on does not skip the state — it applies the confirm
 * transition immediately, so the audit trail still shows both steps.
 */
export const INITIAL_STATUS: OrderStatus = 'pending_confirm';

/** Where `pending_confirm` goes once the merchant (or auto-confirm) accepts. */
export function statusAfterConfirm(paymentType: PaymentType): OrderStatus {
  return paymentType === 'prepaid' ? 'awaiting_payment' : 'confirmed';
}

/**
 * Timestamp column to stamp on entering a status. Kept here so the DB write
 * path cannot forget one and leave an order with a status but no time.
 */
export const STATUS_TIMESTAMP_COLUMN: Partial<Record<OrderStatus, string>> = {
  pending_confirm: 'placedAt',
  awaiting_payment: 'confirmedAt',
  confirmed: 'confirmedAt',
  paid: 'paidAt',
  shipped: 'shippedAt',
  collected: 'collectedAt',
  remitted: 'remittedAt',
  /**
   * "Confirmed" and "scheduled" are the same moment for a booking: the
   * appointment is agreed. The time the appointment is FOR lives on the slot
   * hold, not here.
   */
  scheduled: 'confirmedAt',
  completed: 'completedAt',
  cancelled: 'cancelledAt',
  // `no_show` gets no column deliberately — stamping `completedAt` would say
  // the service happened. The order event log carries when it was marked.
};

/**
 * Human-facing progression shown in the storefront's order tracker.
 *
 * Takes the vertical because the same prepaid payment type means two different
 * journeys: a retail customer is waiting for a parcel, a salon customer is
 * waiting for Thursday. Showing "shipped" on an appointment would be nonsense.
 */
export function statusTimeline(
  paymentType: PaymentType,
  vertical: 'retail' | 'fnb' | 'services' | 'digital' = 'retail',
): OrderStatus[] {
  /**
   * §4 `digital`: the goods are a code list, so the order ends where the money
   * does. The bot delivers the instant the transfer clears — a `fulfilling`
   * step would be a status nobody is ever in, and there is no COD branch at
   * all, because nobody hands over a voucher on credit.
   */
  if (vertical === 'digital') {
    return ['pending_confirm', 'awaiting_payment', 'paid', 'completed'];
  }

  if (vertical === 'services') {
    return paymentType === 'prepaid'
      ? ['pending_confirm', 'awaiting_payment', 'scheduled', 'completed']
      : ['pending_confirm', 'scheduled', 'completed'];
  }

  if (vertical === 'fnb') {
    // §4: no provincial shipping and no COD remittance here — an F&B order is
    // accepted, prepared, and handed over.
    return paymentType === 'prepaid'
      ? ['pending_confirm', 'awaiting_payment', 'paid', 'fulfilling', 'completed']
      : ['pending_confirm', 'confirmed', 'fulfilling', 'completed'];
  }

  return paymentType === 'prepaid'
    ? ['pending_confirm', 'awaiting_payment', 'paid', 'fulfilling', 'shipped', 'completed']
    : ['pending_confirm', 'confirmed', 'shipped', 'collected', 'remitted', 'completed'];
}
